import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import styles from './email-campaign-scheduler-best-time-control.module.scss';
import {Checkbox} from '@Components/checkbox';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {
  getReadableTimeFromUtcTimestamp,
  isOptimalSendTimeforScheduledEmailsValid,
} from '@Components/email-marketing-campaigns/components/email-campaign-scheduler-best-time-control/email-campaign-scheduler-best-time-library';
import type {MaxOptimalTimePayload, OptimalTimeResponse} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import aiGradientSpinner from '@Assets/ai-gradient-spinner.svg';
import {noop} from '@Utils/general.util';
import {computeClientTimezoneOffset} from '@Utils/date.util';
import {ClickableDiv} from '@Components/clickable-div';

export interface EmailCampaignSchedulerBestTimeControlProps {
  onToggleBestTimeCallback: (isBestTimeChecked: boolean, bestTimeUtcTimestamp: number) => void;
  dateTimeChosen: Date;
  containerClasses?: string;
  manuallyTriggerCheck?: boolean;
  emailMarketingCampaignId: string;
  onBestTimeLoadCallback?: (bestTimeUtcTimestamp: number) => void;
}

export function EmailCampaignSchedulerBestTimeControl({
  onToggleBestTimeCallback,
  dateTimeChosen,
  containerClasses = '',
  emailMarketingCampaignId,
  manuallyTriggerCheck,
  onBestTimeLoadCallback = noop,
}: EmailCampaignSchedulerBestTimeControlProps): ReactElement<any> {
  const [shouldSendAtBestTime, setShouldSendAtBestTime] = useState(false);
  const [bestTime, setBestTime] = useState<number | null>(null);
  const [bestTimeError, setBestTimeError] = useState(false);

  useEffect(() => {
    if (manuallyTriggerCheck === undefined || bestTime === null) {
      return;
    }

    if (manuallyTriggerCheck) {
      setShouldSendAtBestTime(true);
      onToggleBestTimeCallback(true, bestTime);
    } else {
      setShouldSendAtBestTime(false);
      onToggleBestTimeCallback(false, bestTime);
    }
  }, [manuallyTriggerCheck, bestTime]);

  useEffect(() => {
    const maxOptimalTimePayload: MaxOptimalTimePayload = {
      idEmailMarketingCampaign: emailMarketingCampaignId,
      clientTimezoneOffset: computeClientTimezoneOffset(),
      scheduledDate: dateTimeChosen.toString(),
    };

    window.PMW.writeLocal('emailmarketing/getOptimalSendingTimeForScheduledEmails', maxOptimalTimePayload)
      .then((response: OptimalTimeResponse) => {
        if (!isOptimalSendTimeforScheduledEmailsValid(response)) {
          setBestTimeError(true);
        } else {
          setBestTimeError(false);
        }

        setBestTime(response.maximumUtcTimestamp);
        onBestTimeLoadCallback(response.maximumUtcTimestamp);
      })
      .catch((error: unknown): void => {
        console.log('Error fetching optimal time: ', error);
      });
  }, []);

  const onCheck = (): void => {
    if (bestTime === null || bestTimeError) {
      return;
    }

    const updatedValue = !shouldSendAtBestTime;
    onToggleBestTimeCallback(updatedValue, bestTime);
    setShouldSendAtBestTime(updatedValue);
  };

  const getCheckboxClasses = (): string => {
    if (bestTimeError) {
      return styles.disabled;
    }

    if (shouldSendAtBestTime) {
      return styles.checked;
    }

    return styles.unchecked;
  };

  const getLoaderIfNeeded = (): ReactElement<any> | null => {
    if (bestTime) {
      return null;
    }

    return (
      <div className={styles.gradientLoader}>
        <Thumbnail
          data={{
            type: ThumbnailType.IMAGE,
            imageSrc: aiGradientSpinner as string,
          }}
          className={styles.gradientSVGSpinner}
        />
      </div>
    );
  };

  const getBestTimeText = (): ReactElement<any> | null => {
    if (bestTime && !bestTimeError) {
      return <Text bold size={TextSize.XSMALL} className="content-ai spacing-m-l-1 body-md-bold" val={getReadableTimeFromUtcTimestamp(bestTime)} />;
    }

    if (bestTimeError) {
      return <Text bold size={TextSize.XSMALL} className="spacing-m-l-1 body-md-bold" val={window.i18next.t('pmwjs_passed')} />;
    }

    return null;
  };

  const getTextSeparator = (): ReactElement<any> | null => {
    if (bestTime) {
      return <div className={styles.dotClasses} />;
    }

    return null;
  };

  return (
    <div className={styles.controlsContainer}>
      <ClickableDiv onClick={onCheck} className={`${styles.controlsWrapper} ${containerClasses} ${bestTime === null || bestTimeError ? styles.error : null}`}>
        <Checkbox
          onClick={onCheck}
          disabled={bestTime === null || bestTimeError}
          className={`${styles.checkbox}  ${getCheckboxClasses()}`}
          isChecked={shouldSendAtBestTime && bestTime !== null}
          size={IconSize.SIZE_ICON_16}
        />
        <Icon className={styles.iconClasses} icon="icon-sparkle" size={IconSize.SIZE_ICON_20} />
        <Text className={`${styles.bestTimeDescription} body-md-regular spacing-m-r-1`} val={window.i18next.t('pmwjs_best_time')} />
        {getTextSeparator()}
        {getLoaderIfNeeded()}
        {getBestTimeText()}
      </ClickableDiv>
    </div>
  );
}
